import { Localized } from "@fluent/react"
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded"
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded"
import { Alert, AlertTitle, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useState } from "react"
import { useAppDispatch } from "../../../app/hooks"
import { AppId, AppTextFieldHandler } from "../../../app/appTypes"
import { DocumentActions, DocumentActionValidationResponse, DocumentWorkflowActions, performDocumentAction, validateDocumentAction } from "../documentsApi"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { ActionContent, ActionTitle } from "./WorkflowActionUtils"
import { CapsLockWarning } from "../../../app/components/CapsLockWarning"
import { CancelButton } from "../../../app/components/CancelButton"
import { YesButton } from "../../../app/components/YesButton"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

const RequiredError = () => <Localized id="field-is-required"><span>_Pole jest wymagane</span></Localized>

const actionsWithDeadline: DocumentWorkflowActions[] = [
    "SendToOpinion",
    "ResendToOpinion",
    "SendToApprove"
];

const actionsWithComment: DocumentWorkflowActions[] = [
    "Reject",
    "WithdrawFromOpinion",
];

const destructiveActions: DocumentWorkflowActions[] = [
    "Reject",
    "WithdrawFromOpinion",
    "ForceFinishOpinion",
    "ForceFinishApproval",
];

const getLocalISODate = (date: Date | number): string => {
    const value = typeof date === "number" ? new Date(date) : date;
    const tzOffset = value.getTimezoneOffset() * 60000;

    return new Date(value.getTime() - tzOffset)
        .toISOString()
        .split("T")[0];
};

const getDefaultDeadline = (): string => {
    const date = new Date();
    date.setDate(date.getDate() + 7);

    return getLocalISODate(date);
};

export interface WorkflowActionRunnerProps {
    documentId: AppId
    action: DocumentWorkflowActions
    onSuccess: (actions: DocumentActions) => void
    onCancel: () => void
}

export const WorkflowActionRunner = ({ documentId, action, onSuccess, onCancel }: WorkflowActionRunnerProps) => {
    const dispatch = useAppDispatch()
    const minAllowedDeadline = getLocalISODate(new Date())
    const [deadline, setDeadline] = useState(getDefaultDeadline);
    const [comment, setComment] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [isCapsLockOn, setIsCapsLockOn] = useState(false)
    const [running, setRunning] = useState(false)
    const [serverError, setServerError] = useState("")
    const [validationErrors, setValidationErrors] = useState(false);
    const [validation, setValidation] =
        useState<DocumentActionValidationResponse | null>(null)
    const [forceExecution, setForceExecution] = useState(false);

    const [validationLoading, setValidationLoading] = useState(false)
    const hasBlockingErrors = (validation?.errors?.length ?? 0) > 0
    const hasWarningsOnly =
        !hasBlockingErrors && (validation?.warnings?.length ?? 0) > 0
    const hasValidationMessages = hasBlockingErrors || hasWarningsOnly

    const actionHasDeadline = actionsWithDeadline.includes(action);
    const actionHasComment = actionsWithComment.includes(action);

    const passwordError = validationErrors && (password === "" || password === null)
    const commentRequired = actionHasComment || (hasWarningsOnly && forceExecution)
    const commentError = validationErrors && commentRequired && (comment === "" || comment === null)
    const deadlineError =
        validationErrors && actionHasDeadline && (deadline === "" || deadline === null || deadline < minAllowedDeadline)

    const isDestructiveAction = destructiveActions.includes(action);
    const actionContentAlertSeverity = (hasWarningsOnly || isDestructiveAction) ? "warning" : "info";


    const runValidation = useCallback(async () => {
        setValidationLoading(true);
        setServerError("");
        setForceExecution(false); // jeśli masz checkbox potwierdzenia warningów

        const result = await dispatch(
            validateDocumentAction({ documentId, action })
        );

        if (validateDocumentAction.fulfilled.match(result)) {
            setValidation(result.payload);
        } else {
            setValidation({
                errors: ["validation-failed"],
                warnings: [],
            });
        }

        setValidationLoading(false);
    }, [dispatch, documentId, action]);

    useEffect(() => {
        runValidation();
    }, [runValidation]);


    const canSubmitAction = (): boolean => {
        return (
            !hasBlockingErrors &&
            (!hasWarningsOnly || forceExecution) &&
            !validationLoading &&
            !running
        );
    };

    const canRunAction = (): boolean => {
        return (
            !!password &&
            (!commentRequired || !!comment) &&
            (!actionHasDeadline || (!!deadline && deadline >= minAllowedDeadline)) &&
            !hasBlockingErrors &&
            (!hasWarningsOnly || forceExecution) &&
            !validationLoading &&
            !running
        );
    };

    const run = async () => {
        setValidationErrors(true);
        setServerError("");

        if (canRunAction()) {
            try {

                setRunning(true)
                const safeDeadline = deadline && deadline < minAllowedDeadline ? minAllowedDeadline : deadline

                const result = await dispatch(performDocumentAction({
                    documentId: documentId,
                    actionPayload: {
                        action: action,
                        password: password,
                        deadline: actionHasDeadline ? safeDeadline : undefined,
                        comment: comment || undefined,
                        forceExecution: hasWarningsOnly ? forceExecution : false
                    }
                }))
                if (performDocumentAction.fulfilled.match(result)) {
                    const actions = result.payload as DocumentActions;
                    dispatch(showSuccess("wfl-action-completed"))
                    onSuccess(actions)
                }
                else {
                    let errorMsg = "error"
                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title

                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setRunning(false)
            }
        }
    }

    const handleCommentFieldChange: AppTextFieldHandler = useCallback(e => {
        setComment(e.target.value)
    }, [])

    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(e => {
        setPassword(e.target.value)
    }, [])

    const handlePasswordFieldKeyEvent = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
        setIsCapsLockOn(event.getModifierState("CapsLock"))
    }, [])

    const handlePasswordFieldBlur = useCallback(() => {
        setIsCapsLockOn(false)
    }, [])

    const handlePasswordVisibilityToggle = useCallback(() => {
        setShowPassword(current => !current)
    }, [])

    const handleDeadlineFieldChange: AppTextFieldHandler = useCallback(e => {
        const value = e.target.value
        setDeadline(value && value < minAllowedDeadline ? minAllowedDeadline : value)
    }, [minAllowedDeadline])


    return <Dialog open={true} maxWidth="xs" fullWidth>
        <DialogTitle>
            <ActionTitle action={action} />
        </DialogTitle>
        <DialogContent>
            <DialogContentText>
                <Alert
                    severity={actionContentAlertSeverity}
                    variant="outlined"
                    sx={{
                        mb: 2,
                        mt: 2,
                        alignItems: "flex-start",
                        "& .MuiAlert-message": {
                            width: "100%",
                        },
                    }}
                >
                    <Typography variant="body2">
                        <ActionContent action={action} />
                    </Typography>

                    {hasWarningsOnly && (
                        <>
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                {validation!.warnings.map((w, i) => (
                                    <Stack key={i} spacing={0.25}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {i + 1}.{" "}
                                            <Localized id={w}>
                                                <span>_{w}</span>
                                            </Localized>
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ pl: 2.5 }}
                                        >
                                            <Localized id={`${w}-continue-description`}>
                                                <span />
                                            </Localized>
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            <FormControlLabel
                                sx={{ mt: 1, ml: 0 }}
                                control={
                                    <Checkbox
                                        checked={forceExecution}
                                        onChange={(e) => setForceExecution(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        <Localized id="document-action-force-execution-confirmation">
                                            <span>_Kontynuować mimo ostrzeżeń?</span>
                                        </Localized>
                                    </Typography>
                                }
                            />
                        </>
                    )}
                </Alert>
            </DialogContentText>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2} sx={{ mt: 2 }}>
                {(actionHasComment || (hasWarningsOnly && forceExecution)) && (
                    <TextField
                        autoFocus={true}
                        required={commentRequired}
                        helperText={commentError && <RequiredError />}
                        error={commentError}
                        fullWidth
                        value={comment}
                        multiline
                        minRows={1}
                        maxRows={5}
                        InputLabelProps={{ shrink: true }}
                        onChange={handleCommentFieldChange}
                        label={<Localized id="document-action-reason"><span>_Przyczyna</span></Localized>}
                    />
                )}

                {actionHasDeadline && (
                    <TextField
                        type="date"
                        fullWidth
                        value={deadline}
                        error={deadlineError}
                        autoFocus={!actionHasComment}
                        helperText={deadlineError && <RequiredError />}
                        InputLabelProps={{ shrink: true }}
                        required
                        inputProps={{
                            min: minAllowedDeadline
                        }}
                        onChange={handleDeadlineFieldChange}
                        label={<Localized id="deadline"><span>_Termin</span></Localized>}
                    />
                )}

                <TextField
                    fullWidth
                    error={passwordError}
                    autoFocus={!actionHasComment && !actionHasDeadline}
                    type={showPassword ? "text" : "password"}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    edge="end"
                                    onClick={handlePasswordVisibilityToggle}
                                    onMouseDown={(event) => event.preventDefault()}
                                    aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                                >
                                    {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    value={password}
                    onChange={handlePasswordFieldChange}
                    onKeyDown={handlePasswordFieldKeyEvent}
                    onKeyUp={handlePasswordFieldKeyEvent}
                    onBlur={handlePasswordFieldBlur}
                    label={<Localized id="password"><span>_Hasło</span></Localized>}
                    helperText={
                        passwordError ? (
                            <RequiredError />
                        ) : isCapsLockOn ? (
                            <CapsLockWarning />
                        ) : (
                            <Localized id="autorization-password-required">
                                <span>_Wymagana autoryzacja hasłem</span>
                            </Localized>
                        )
                    }
                    FormHelperTextProps={isCapsLockOn ? {
                        sx: {
                            ml: 0,
                            mt: 1,
                        }
                    } : undefined}
                    autoComplete="current-password"
                />
            </Stack>

            {(serverError || validationLoading || hasBlockingErrors) && (
                <Divider sx={{ mt: 3, mb: 1 }} />
            )}

            {serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                    <Localized id={serverError}>
                        <span>Server error</span>
                    </Localized>
                </Alert>
            }
            {validationLoading && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    <Localized id="document-validating-action">
                        <span>_Sprawdzam możliwość wykonania akcji…</span>
                    </Localized>
                </Alert>
            )}

            {hasBlockingErrors && (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={runValidation}
                            disabled={validationLoading || running}
                            aria-label="Odśwież walidację"
                        >
                            <RefreshRoundedIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    <AlertTitle>
                        <Localized id="document-action-not-allowed">
                            <span>_Nie można wykonać tej akcji:</span>
                        </Localized>
                    </AlertTitle>
                    <Stack spacing={0.5}>
                        {validation!.errors.map((e, i) => (
                            <Typography key={i} variant="body2">
                                <Localized id={e}>
                                    <span>_{e}</span>
                                </Localized>
                            </Typography>
                        ))}
                    </Stack>
                </Alert>
            )}
        </DialogContent>
        <DialogActions>
            <YesButton
                loading={running}
                onClick={run}
                disabled={!canSubmitAction()}
            />
            <CancelButton loading={running} disabled={running} onClick={onCancel}></CancelButton>
        </DialogActions>
    </Dialog>
}

